import React, { useMemo } from 'react';

import { Button } from 'components';
import { useTranslation } from 'react-i18next';

import type {
  VisitorManagementFeatureProperties,
  VisitorType,
} from 'application-types';

import useFlag from 'lane-shared/hooks/useFlag';
import { FeatureFlag } from 'constants-flags';
import { buildWorkbookConfig } from 'domains/visitorManagement/flatfile/workbook';
import type { FormCustomFieldColumn } from 'domains/visitorManagement/flatfile/workbook';
import { listener, MAX_CSV_ROWS } from 'domains/visitorManagement/flatfile/listener';

import styles from 'domains/visitorManagement/content-blocks/AddVisitorsBlock.scss';
import { Space, useFlatfile, useListener, Workbook } from '@flatfile/react';

const EMPTY_FORM_CUSTOM_FIELD_COLUMNS: FormCustomFieldColumn[] = [];

const EMPTY_EXISTING_VISITORS: VisitorType[] = [];

export function VisitorInviteBulkUpload({
  properties,
  handleBulkAdd,
  isPreview = false,
  formCustomFieldColumns = EMPTY_FORM_CUSTOM_FIELD_COLUMNS,
  customFieldColumnsLoading = false,
  existingVisitors = EMPTY_EXISTING_VISITORS,
}: {
  properties: VisitorManagementFeatureProperties;
  handleBulkAdd: (visitors: VisitorType[]) => void;
  isPreview?: boolean;
  /** Resolved from `useFormCustomFieldColumns` in the parent (workbook + listener + persistence). */
  formCustomFieldColumns?: FormCustomFieldColumn[];
  /** While configs are loading, disable open to avoid a workbook missing custom columns. */
  customFieldColumnsLoading?: boolean;
  /** Visitors already accepted from a previously submitted CSV in this session. */
  existingVisitors?: VisitorType[];
}): React.ReactElement {
  const { t } = useTranslation();
  const { open, openPortal, closePortal } = useFlatfile();
  const customFieldsEnabled = useFlag(
    FeatureFlag.VisitorManagementCustomFields,
    false
  );
  const maxCsvRows = useFlag(
    FeatureFlag.VisitorManagementReducedBulkUploadLimit,
    MAX_CSV_ROWS
  );
  const isDuplicateEmailValidationEnabled = useFlag<boolean>(
    FeatureFlag.VisitorManagementDuplicateEmailValidation,
    false
  );

  const workbookConfig = useMemo(
    () => buildWorkbookConfig(formCustomFieldColumns),
    [formCustomFieldColumns]
  );

  const flatfileListener = useMemo(
    () =>
      listener(
        properties,
        handleBulkAdd,
        closePortal,
        formCustomFieldColumns,
        customFieldsEnabled,
        maxCsvRows,
        isDuplicateEmailValidationEnabled ? existingVisitors : EMPTY_EXISTING_VISITORS
      ),
    [
      properties,
      handleBulkAdd,
      closePortal,
      formCustomFieldColumns,
      customFieldsEnabled,
      maxCsvRows,
      isDuplicateEmailValidationEnabled,
      existingVisitors,
    ]
  );

  useListener(flatfileListener);

  const handleOnClick = () => {
    if (!open) {
      openPortal();
    } else {
      closePortal();
    }
  };

  return (
    <>
      <Button
        onClick={handleOnClick}
        className={styles.bulkUploadButton}
        disabled={isPreview || customFieldColumnsLoading}
        testId="inviteBulkUpload"
      >
        {t('web.content.feature.visitorManagement.visitorInvite.bulkUpload')}
      </Button>
      <Space
        config={{
          name: 'Activate Visitors Space',
          metadata: {
            sidebarConfig: {
              showSidebar: true,
            },
          },
        }}
      >
        {/* Key forces remount when custom columns change so Flatfile sheet shows correct columns */}
        <Workbook
          key={
            formCustomFieldColumns.length > 0
              ? formCustomFieldColumns.map(c => c.fieldKey).join(',')
              : 'default'
          }
          config={workbookConfig}
        />
      </Space>
    </>
  );
}
